import { Theme } from "src/global/ui/themes/theme.ts"
import { styled, css } from "styled-components"
import { Card, Flex } from "antd"
import { NativeCloudSupportedDataSource } from "src/pages/DataAssets/schemas/data-source-schemas.ts"
import { PaintedDoorDataSource } from "src/pages/DataAssets/getImageLogo.ts"
import { Tag } from "src/global/ui/Tag/Tag"
import { Image } from "src/global/ui/Image/Image.tsx"

import { useAnalytics } from "src/global/hooks/analytics/useAnalytics.ts"
import { Heading3 } from "src/global/ui/typography/Text/Text"
import { StyledLogoContainer } from "src/global/ui/List/MainContentList"
import { images } from "src/global/ui/Image/images"
import { DatasourceTypeV2 } from "src/api/graphql/graphql"

interface StyledCardProps {
  $supportedType: boolean
  theme: Theme
  $isAPI?: boolean
  onClick?: () => void
}

const StyledCard = styled(Card)<StyledCardProps>`
  ${({ $supportedType, theme, $isAPI }) => css`
    min-height: 100px;
    width: 225px;
    cursor: ${$supportedType ? "pointer" : "not-allowed"};
    background: ${$supportedType
      ? theme.colors.neutralColorPalette.whites.white
      : theme.colors.neutralColorPalette.backgroundsAndBorders.gxSurfaceSecondary};
    border-color: ${theme.colors.neutralColorPalette.backgroundsAndBorders.gxBorder};

    .ant-card-body {
      padding-top: ${$isAPI ? "18px" : theme.spacing.s};
      padding-bottom: 0;
      display: flex;
      align-items: center;
      min-height: calc(100px - ${theme.spacing.s}); /* Card height minus top/bottom padding */
    }
  `}
`

const StyledLink = styled.a`
  display: block;
  text-decoration: none;
  color: inherit;
`

const StyledTag = styled(Tag)`
  ${({ theme }) => css`
    width: fit-content;
    color: ${theme.colors.neutralColorPalette.blacks.colorTextTertiary};
    font-weight: 400;
    font-size: ${theme.typography.fontSize.extraSmall};
    margin: 0;
  `}
`

type BaseDataSourceCardProps = {
  text?: string
  type?: NativeCloudSupportedDataSource | PaintedDoorDataSource
  isDSPaintedDoorsEnabled?: boolean // TODO: Remove this prop when cleaned up DS Painted Door FF
  image?: React.ReactNode
  onClick?: (type?: NativeCloudSupportedDataSource | PaintedDoorDataSource) => void
  href?: string
}

export function DataSourceCard({ text: textProp, type, image: imageProp, onClick, href }: BaseDataSourceCardProps) {
  const isAPI = Boolean(href)
  const isSupportedType = isAPI || Boolean(onClick)
  const analytics = useAnalytics()
  const isCustomCard = imageProp && textProp && !type

  const handleClick = () => {
    analytics.capture("add_data_source_card_selected.datasource", {
      type: type,
      url: href,
    })

    onClick?.(type)
  }

  const image = type && dataSourceTypeMap[type]?.image

  const dataSourceCardContent = (
    <Flex
      align="center"
      vertical
      gap={href ? 2 : undefined}
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      {image && <Image type={image} />}
      {href && <StyledTag>API setup</StyledTag>}
    </Flex>
  )

  if (isCustomCard) {
    return (
      <StyledCard hoverable onClick={handleClick} role="button" $supportedType={isSupportedType}>
        <Flex vertical gap={8}>
          <Card.Meta
            description={<Heading3>{textProp}</Heading3>}
            avatar={<StyledLogoContainer>{imageProp}</StyledLogoContainer>}
          />
        </Flex>
      </StyledCard>
    )
  }

  if (href) {
    return (
      <StyledLink href={href} target="_blank" onClick={handleClick}>
        <StyledCard $isAPI hoverable $supportedType={isSupportedType}>
          {dataSourceCardContent}
        </StyledCard>
      </StyledLink>
    )
  }

  return (
    <StyledCard hoverable onClick={handleClick} role="button" $supportedType={isSupportedType}>
      {dataSourceCardContent}
    </StyledCard>
  )
}

type DataSourceTypeMap = Record<
  DatasourceTypeV2 | PaintedDoorDataSource,
  { humanReadableName: string; image?: keyof typeof images }
>

export const dataSourceTypeMap: DataSourceTypeMap = {
  DATABRICKS_SQL: {
    humanReadableName: "Databricks SQL",
    image: "databricksLogo",
  },
  PANDAS: {
    humanReadableName: "pandas",
    image: "pandasLogo",
  },

  POSTGRES: {
    humanReadableName: "PostgreSQL",
    image: "postgreSQLLogo",
  },
  REDSHIFT: {
    humanReadableName: "Redshift",
    image: "redShiftLogo",
  },
  SNOWFLAKE: {
    humanReadableName: "Snowflake",
    image: "snowflakeLogo",
  },
  SPARK: {
    humanReadableName: "Spark",
    image: "sparkLogo",
  },
  NEON: {
    humanReadableName: "Neon",
    image: "neonLogo",
  },
  BIGQUERY: {
    humanReadableName: "BigQuery",
    image: "googleBigQueryLogo",
  },
  CITUS: {
    humanReadableName: "Citus",
    image: "citusDBLogo",
  },
  ALLOY: {
    humanReadableName: "AlloyDB",
    image: "alloyDBLogo",
  },
  AURORA: {
    humanReadableName: "Amazon Aurora PostgreSQL",
    image: "auroraPostgreSQLLogo",
  },
  PANDAS_ABS: {
    humanReadableName: "Azure Blob Storage",
    image: "azureBlobStorageLogo",
  },
  PANDAS_GCS: {
    humanReadableName: "Google Cloud Storage",
    image: "googleCloudStorageLogo",
  },
  PANDAS_S3: {
    humanReadableName: "Amazon S3",
    image: "amazonS3Logo",
  },
  PANDAS_FILESYSTEM: {
    humanReadableName: "Pandas",
    image: "pandasLogo",
  },
  PANDAS_DBFS: {
    humanReadableName: "Pandas",
    image: "pandasLogo",
  },
  SPARK_DBFS: {
    humanReadableName: "Spark",
    image: "sparkLogo",
  },
  SPARK_FILESYSTEM: {
    humanReadableName: "Spark",
    image: "sparkLogo",
  },
  SPARK_S3: {
    humanReadableName: "Spark",
    image: "sparkLogo",
  },
  SPARK_ABS: {
    humanReadableName: "Spark",
    image: "sparkLogo",
  },
  SPARK_GCS: {
    humanReadableName: "Spark",
    image: "sparkLogo",
  },
  SQL: {
    humanReadableName: "SQL",
    image: undefined,
  },
  SQLITE: {
    humanReadableName: "SQLite",
    image: undefined,
  },
}
